import { Link } from "react-router-dom";
import { ResponsiveCoverImage, type CoverImageVariants } from "@/components/blog/ResponsiveCoverImage";
import { estimateReadingTime } from "@/lib/blog/readingTime";
import { audienceBadgeLabel } from "@/lib/blog/blogFilters";

interface Props {
  slug: string;
  title: string;
  excerpt: string | null;
  coverImageUrl: string | null;
  coverImageVariants?: CoverImageVariants | null;
  publishedAt: string;
  bodyMd?: string | null;
  outcomeSummary?: string | null;
  targetAudience?: string | null;
  /**
   * Precomputed reading time in minutes. When supplied (e.g. from the
   * archive boot payload, which doesn't ship `body_md`), it is used
   * verbatim. Otherwise we fall back to estimating from `bodyMd ?? excerpt`.
   */
  readingMinutes?: number | null;
}

function formatDate(iso: string) {
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

export function BlogPostCard({ slug, title, excerpt, coverImageUrl, coverImageVariants, publishedAt, bodyMd, outcomeSummary, targetAudience, readingMinutes }: Props) {
  const audienceLabel = audienceBadgeLabel(targetAudience);
  const minutes =
    typeof readingMinutes === "number" && readingMinutes > 0
      ? readingMinutes
      : estimateReadingTime(bodyMd ?? excerpt ?? "");
  const trimmedOutcome = outcomeSummary?.trim() || "";
  const hasOutcome = trimmedOutcome.length > 0;
  return (
    <article className="group rounded-3xl border border-border/60 overflow-hidden bg-card shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all">
      <Link
        to={`/blog/${slug}/`}
        className="flex h-full flex-col focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-3xl"
      >
        <div className="aspect-[16/9] overflow-hidden bg-muted border-b border-border/60">
          <ResponsiveCoverImage
            variants={coverImageVariants}
            src={coverImageUrl}
            alt={title}
            slug={slug}
            sizes="(min-width: 1280px) 360px, (min-width: 640px) 45vw, 100vw"
            imgClassName="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
          />
        </div>
        <div className="flex flex-col flex-1 p-6 md:p-7">
          <div className="mb-3">
            <span
              className="inline-flex items-center h-6 px-2.5 rounded-full bg-primary/10 text-primary text-caption font-semibold"
              data-testid="audience-badge"
            >
              {audienceLabel}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-caption mb-3 text-muted-foreground">
            <time dateTime={publishedAt} className="tabular-nums">
              {formatDate(publishedAt)}
            </time>
            <span aria-hidden="true" className="text-muted-foreground/50">·</span>
            <span>{minutes} min read</span>
          </div>
          <h3 className="text-h3 font-semibold mb-3 group-hover:text-primary transition-colors">
            {title}
          </h3>
          {hasOutcome ? (
            <>
              <div className="rounded-2xl bg-primary/5 border border-primary/15 p-4 mb-3">
                <p className="text-caption font-semibold uppercase text-primary mb-1">
                  What you'll get
                </p>
                <p className="text-body text-foreground/90 leading-snug line-clamp-3">
                  {trimmedOutcome}
                </p>
              </div>
              {excerpt && (
                <p className="text-body-sm text-muted-foreground line-clamp-2 mb-4">
                  {excerpt}
                </p>
              )}
            </>
          ) : (
            excerpt && (
              <p className="text-body text-muted-foreground line-clamp-3 mb-4">
                {excerpt}
              </p>
            )
          )}
          <span className="mt-auto inline-flex items-center text-primary font-semibold text-body-sm group-hover:translate-x-0.5 transition-transform">
            Read guide →
          </span>
        </div>
      </Link>
    </article>
  );
}